import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

interface SegmentedControlOption<Value extends string = string> {
  value: Value;
  label: React.ReactNode;
  disabled?: boolean;
  /** Подсказка при наведении и фокусе; нужна опциям, у которых метка — иконка. */
  tooltip?: React.ReactNode;
  /** Доступное имя кнопки, когда метка — иконка и текста у неё нет. */
  ariaLabel?: string;
  /** Метка — только значок: при `size="sm"` пункт становится узким (24×22). */
  iconOnly?: boolean;
}

const segmentedControlVariants = cva(
  "group/segmented-control inline-flex w-fit items-center justify-center",
  {
    variants: {
      variant: {
        default: "",
      },
      size: {
        default: "gap-[3px] rounded-lg bg-muted p-[3px]",
        // Плотная полоса фильтров: контейнер 26 px, пункт 22 px.
        sm: "h-[26px] gap-[2px] rounded-sm bg-secondary p-[2px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const segmentedControlOptionVariants = cva(
  "relative inline-flex items-center justify-center border border-transparent whitespace-nowrap transition-all hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 aria-pressed:text-foreground",
  {
    variants: {
      size: {
        default:
          "h-[calc(100%-1px)] gap-1.5 rounded-md px-1.5 py-0.5 text-sm font-medium text-foreground/60 aria-pressed:bg-background aria-pressed:shadow-sm dark:aria-pressed:border-input dark:aria-pressed:bg-input/30",
        sm: "h-[22px] gap-1.5 rounded-[4px] px-[9px] text-[12px] font-medium text-muted-foreground aria-pressed:bg-background",
      },
      iconOnly: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [{ size: "sm", iconOnly: true, className: "w-6 px-0" }],
    defaultVariants: {
      size: "default",
      iconOnly: false,
    },
  },
);

interface SegmentedControlProps<Value extends string = string>
  extends Omit<React.ComponentProps<"div">, "children" | "onChange">,
    VariantProps<typeof segmentedControlVariants> {
  options: ReadonlyArray<SegmentedControlOption<Value>>;
  value: Value;
  onValueChange: (value: Value) => void;
  /** Имя группы для читающих с экрана — без него `role="group"` безымянный. */
  label: string;
}

function SegmentedControl<Value extends string = string>({
  options,
  value,
  onValueChange,
  className,
  variant,
  size,
  label,
  ...props
}: SegmentedControlProps<Value>) {
  // `<fieldset>` не годится для группы кнопок-переключателей — это не форма,
  // а строка взаимоисключающих действий (WAI-ARIA APG «toolbar»); `role="group"`
  // с `aria-label` — тот же паттерн, что и у ARIA tablist, который этот
  // компонент заменяет (review, пункт 6).
  return (
    // biome-ignore lint/a11y/useSemanticElements: смотри комментарий выше
    <div
      data-slot="segmented-control"
      role="group"
      aria-label={label}
      className={cn(segmentedControlVariants({ variant, size }), className)}
      {...props}
    >
      {options.map((option) => {
        const buttonProps = {
          type: "button",
          "data-slot": "segmented-control-option",
          "aria-pressed": option.value === value,
          "aria-label": option.ariaLabel,
          disabled: option.disabled,
          onClick: () => onValueChange(option.value),
          className: cn(
            segmentedControlOptionVariants({ size, iconOnly: option.iconOnly === true }),
          ),
        } as const;
        if (option.tooltip === undefined) {
          return (
            <button key={option.value} {...buttonProps}>
              {option.label}
            </button>
          );
        }
        return (
          <Tooltip key={option.value}>
            <TooltipTrigger render={<button {...buttonProps} />}>{option.label}</TooltipTrigger>
            <TooltipContent>{option.tooltip}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

export type { SegmentedControlOption, SegmentedControlProps };
export { SegmentedControl, segmentedControlVariants };
